// src/services/videoService.js
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  updateDoc,
  doc,
  serverTimestamp,
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase';

const VIDEOS_COLLECTION = 'videos';

export const videoService = {
  /**
   * Get all videos from Firestore
   * @returns {Promise<Array>} - Array of video objects
   */
  async getAllVideos() {
    try {
      const videosRef = collection(db, VIDEOS_COLLECTION);
      const q = query(videosRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting videos:', error);
      throw error;
    }
  },

  /**
   * Get videos with filters applied
   * @param {Object} filters - Filter criteria
   * @returns {Promise<Array>} - Filtered array of videos
   */
  async getFilteredVideos(filters) {
    try {
      let q = collection(db, VIDEOS_COLLECTION);
      const constraints = [];
      
      // Add filter constraints
      if (filters.bodyPart) {
        constraints.push(where('bodyPart', '==', filters.bodyPart));
      }
      if (filters.exerciseType) {
        constraints.push(where('exerciseType', '==', filters.exerciseType));
      }
      if (filters.withAccessory !== '') {
        constraints.push(where('withAccessory', '==', filters.withAccessory === 'true'));
      }

      // Apply constraints if any
      if (constraints.length > 0) {
        q = query(q, ...constraints);
      }

      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting filtered videos:', error);
      throw error;
    }
  },

  /**
   * Add a new video to Firestore
   * @param {Object} videoData - Video information
   * @param {string} userEmail - Email of user adding the video
   * @returns {Promise<string>} - ID of created document
   */
  async addVideo(videoData, userEmail) {
    try {
      const videosRef = collection(db, VIDEOS_COLLECTION);
      const docRef = await addDoc(videosRef, {
        ...videoData,
        createdAt: serverTimestamp(),
        createdBy: userEmail
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Error adding video:', error);
      throw error;
    }
  },

  /**
   * Update an existing video
   * @param {string} videoId - ID of video to update
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  async updateVideo(videoId, updates) {
    try {
      const videoRef = doc(db, VIDEOS_COLLECTION, videoId);
      await updateDoc(videoRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating video:', error);
      throw error;
    }
  },

  /**
   * Delete a video from Firestore
   * @param {string} videoId - ID of video to delete
   * @returns {Promise<void>}
   */
  async deleteVideo(videoId) {
    try {
      const videoRef = doc(db, VIDEOS_COLLECTION, videoId);
      await deleteDoc(videoRef);
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  },

  /**
   * Extract YouTube video ID from URL
   * @param {string} url - YouTube URL
   * @returns {string|null} - Video ID or null if invalid
   */
  extractVideoId(url) {
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&\s]+)/,
      /(?:youtu\.be\/)([^&\s]+)/,
      /(?:youtube\.com\/embed\/)([^&\s]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  },

  /**
   * Get thumbnail URL for YouTube video
   * @param {string} videoId - YouTube video ID
   * @returns {string} - Thumbnail URL
   */
  getThumbnailUrl(videoId) {
    return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
  }
};